"use client";

import * as Sentry from "@sentry/nextjs";
import { isNextRouterError } from "next/dist/client/components/is-next-router-error";
import { usePathname, useRouter } from "next/navigation";
import React, { PropsWithChildren } from "react";

function Fallback() {
  const router = useRouter();

  return (
    <div className="-mt-20 flex h-screen flex-col items-center justify-center gap-4">
      <div className="text-2xl text-primary">Oops! That&apos;s our fault.</div>
      <div className="text-primary">
        We&apos;re sorry for the inconvenience. Please try refreshing the page
        or contact support@gondola.ai if the problem persists.
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => router.back()}
          className="rounded-lg border border-border px-4 py-2 text-sm text-primary transition-colors hover:bg-muted"
        >
          Go back
        </button>
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Refresh page
        </button>
      </div>
    </div>
  );
}

interface ErrorBoundaryState {
  error: Error | null;
}

class NextSafeErrorBoundary extends React.Component<
  PropsWithChildren<{ resetKey: string }>,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Let Next.js handle its own internal errors (notFound, redirect)
    if (isNextRouterError(error)) {
      throw error;
    }
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack ?? undefined },
    });
  }

  componentDidUpdate(prevProps: { resetKey: string }) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return <Fallback />;
    }
    return this.props.children;
  }
}

const ErrorBoundaryWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  const pathname = usePathname();

  return (
    <NextSafeErrorBoundary resetKey={pathname}>
      {children}
    </NextSafeErrorBoundary>
  );
};

export default ErrorBoundaryWrapper;
